// Marketing CTA clicks -> GA4 `cta_click` (28 Oct 2026 CTA refresh).
//
// Every button and link on the business marketing pages is measured by its
// label, the page section it sits in and the kind of action, without wiring
// each button by hand. The marketing app (new-hub/OptimizedApp) sets
// <body data-offlico-marketing="1"> while it is mounted; outside it nothing is
// sent. GA4 reports these as event-scoped custom dimensions: cta_label,
// cta_section and cta_kind (GA4 Admin > Custom definitions).
//
// cta_kind:
//   trial  = the Professional 30-day free trial (card at checkout)
//   free   = the free Starter sign-up (no card)
//   signup = another sign-up or sign-in action we can't tell apart
//   link   = a page link (features, pricing, a trade page, contact)
// A button can set data-cta-kind="trial|free|signup|link" when its label alone
// doesn't say which (e.g. "Get started"), and data-cta-to="<path>" to record
// where it goes.

export const MARKETING_BODY_FLAG = "offlicoMarketing";

const MAX_LENGTH = 100;
const KINDS = new Set(["trial", "free", "signup", "link"]);

function clean(value) {
  return String(value || "").replace(/\s+/g, " ").trim().slice(0, MAX_LENGTH);
}

// Tabs, accordions, carousels, menus and toggles are page controls, not CTAs.
function isUiControl(element) {
  if (element.hasAttribute("aria-expanded") || element.hasAttribute("aria-pressed")) return true;
  if (element.hasAttribute("aria-selected")) return true;
  const role = element.getAttribute("role");
  if (role === "tab" || role === "switch" || role === "option" || role === "menuitem") return true;
  if (element.closest('[role="tablist"], [role="listbox"], summary')) return true;
  return false;
}

function sectionName(element) {
  if (element.closest("header")) return "header";
  if (element.closest("footer")) return "footer";
  const section = element.closest("section");
  if (!section) return "page";
  const labelledBy = section.getAttribute("aria-labelledby");
  if (labelledBy) {
    const labelled = clean(section.ownerDocument?.getElementById(labelledBy)?.textContent);
    if (labelled) return labelled;
  }
  const heading = clean(section.querySelector("h1, h2, h3")?.textContent);
  return heading || clean(section.getAttribute("aria-label")) || clean(String(section.className || "").split(" ")[0]) || "section";
}

function parseHref(href) {
  if (!href) return null;
  try {
    return new URL(href, "https://www.offlico.com");
  } catch {
    return null;
  }
}

// Where the CTA goes, as path + query, never with tokens or ad click ids.
export function ctaDestination(href) {
  const url = parseHref(href);
  if (!url) return "";
  for (const key of [...url.searchParams.keys()]) {
    if (/token|code|fbclid|gclid|msclkid|utm_/i.test(key)) url.searchParams.delete(key);
  }
  const query = url.searchParams.toString();
  const sameSite = url.hostname === "www.offlico.com" || url.hostname === "offlico.com";
  return clean((sameSite ? "" : url.host) + url.pathname + (query ? "?" + query : ""));
}

export function ctaKind(label, href) {
  const text = String(label || "").toLowerCase();
  const url = parseHref(href);
  const path = url ? url.pathname.replace(/\/+$/, "") || "/" : "";
  const redirect = url ? url.searchParams.get("redirect") || "" : "";
  if (text.includes("free trial") || text.includes("upgrade to professional") || redirect.includes("checkout=basic")) {
    return "trial";
  }
  if (path === "/signup" || path === "/upgrade" || (path === "/signin" && url?.searchParams.get("mode") === "signup")) {
    return redirect === "/app" ? "free" : "signup";
  }
  if (path === "/signin" || text === "log in") return "signup";
  return "link";
}

// The GA4 parameters for a click on `element`, or null when it isn't a
// marketing CTA (outside the marketing app, a page control, or no label).
export function marketingCtaEvent(element, doc = typeof document === "undefined" ? null : document) {
  if (!element || !doc?.body || doc.body.dataset?.[MARKETING_BODY_FLAG] !== "1") return null;
  const tag = element.tagName;
  if (tag !== "A" && tag !== "BUTTON") return null;
  if (isUiControl(element)) return null;
  const label = clean(element.getAttribute("aria-label") || element.textContent);
  if (!label) return null;
  const href = tag === "A" ? element.getAttribute("href") || "" : element.getAttribute("data-cta-to") || "";
  const declaredKind = element.getAttribute("data-cta-kind");
  return {
    cta_label: label,
    cta_section: sectionName(element),
    cta_kind: KINDS.has(declaredKind) ? declaredKind : ctaKind(label, href),
    cta_destination: ctaDestination(href),
  };
}
